import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bot,
  Palette,
  Plug,
  GraduationCap,
  Code,
  Megaphone,
  Layers,
  CheckCircle2,
  Star,
  PenTool,
} from "lucide-react";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: "blur(6px)" },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

/* ─── SERVICES DATA ─── */
const services = [
  {
    icon: Bot,
    title: "Custom AI Agent Development",
    desc: "For businesses that need AI agents beyond what the no-code builder offers. Custom NLP models, complex decision trees, multi-system integration, and enterprise-grade deployment.",
    features: [
      "Your AI understands your specific business and customers",
      "Customers can have natural conversations without getting stuck",
      "Works seamlessly with the tools you already use",
      "You see how your AI is performing 24/7",
      "Your AI gets smarter based on real usage",
    ],
    startingAt: "₹50,000",
    gradient: "from-violet-500 to-purple-600",
    image:
      "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=200&q=60",
  },
  {
    icon: Palette,
    title: "Brand & Digital Presence",
    desc: "From brand identity to web presence — we build the digital face of your business. Websites, social strategy, content, and visual identity that stands out in your market.",
    features: [
      "Your brand looks consistent everywhere customers see it",
      "Your website works perfectly on every device",
      "Your social media actually grows and engages your audience",
      "Your message resonates with people who are listening",
      "People find you when they search for what you sell",
    ],
    startingAt: "₹25,000",
    gradient: "from-fuchsia-500 to-pink-600",
    image:
      "https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&w=200&q=60",
  },
  {
    icon: Plug,
    title: "Integration & Migration",
    desc: "Already using tools like Tally, Zoho, SAP, or custom software? We connect everything to BusiGrow so your automation works with your existing tech.",
    features: [
      "Your systems work together without manual work",
      "None of your data gets lost in the switch",
      "We build exactly what you need to connect",
      "Every system sees the same up-to-date information",
      "We keep everything connected as systems change",
    ],
    startingAt: "₹30,000",
    gradient: "from-indigo-500 to-blue-600",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=200&q=60",
  },
  {
    icon: GraduationCap,
    title: "Training & Enablement",
    desc: "Help your team master BusiGrow and AI automation. Hands-on training, certification, and ongoing support.",
    features: [
      "Your team knows how to use every feature",
      "Your team can work effectively with AI",
      "Your team actually uses the new tools",
      "Your team has resources they understand",
      "Your team keeps improving month after month",
    ],
    startingAt: "₹10,000",
    gradient: "from-amber-500 to-orange-500",
    image:
      "https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?auto=format&fit=crop&w=200&q=60",
  },
];

/* ─── HERO ─── */
const StudioHero = () => (
  <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-20 overflow-hidden hero-mesh">
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-violet-300/15 rounded-full blur-[120px] animate-float-orb" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-purple-300/10 rounded-full blur-[120px]" />
      {/* Morphing blob */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-br from-violet-400/20 via-purple-300/10 to-indigo-400/20 rounded-full blur-[100px] animate-morph-blob" />
    </div>
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        {/* Left: Text content */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="max-w-3xl"
        >
          <motion.span
            variants={fadeUp}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-sm text-slate-600"
          >
            <Layers className="w-4 h-4 text-violet-600" /> Services Arm
          </motion.span>
          <motion.h1
            variants={fadeUp}
            custom={1}
            className="mt-6 sm:mt-8 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1]"
          >
            BusiGrow <span className="text-gradient">Studio</span>
          </motion.h1>
          <motion.p
            variants={fadeUp}
            custom={2}
            className="mt-4 sm:mt-6 text-base sm:text-lg text-slate-500 max-w-xl leading-relaxed"
          >
            When you need something beyond self-serve. Our expert team builds
            custom AI solutions, brand identities, integrations, and training
            programs — tailored to your business.
          </motion.p>
          <motion.div
            variants={fadeUp}
            custom={3}
            className="mt-8 flex flex-wrap gap-4"
          >
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold hover:shadow-apple-lg transition-all"
            >
              Discuss Your Project
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </motion.div>

        {/* Right: Hero image (hidden on mobile) */}
        <motion.div
          initial={{ opacity: 0, x: 40, scale: 0.95 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="hidden lg:block relative"
        >
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=800&q=80"
              alt="Creative studio workspace"
              className="w-full rounded-3xl shadow-apple-xl object-cover aspect-[4/3] animate-glow-pulse"
              loading="lazy"
            />
            {/* Floating overlay card: Design Studio */}
            <div className="absolute -bottom-4 -left-6 flex items-center gap-3 px-4 py-3 rounded-2xl glass shadow-apple-lg border border-white/40">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <PenTool className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm font-bold text-slate-800">
                Design Studio
              </span>
            </div>
            {/* Floating pill: 24/7 Support */}
            <div className="absolute -top-3 -right-3 px-4 py-2 rounded-full bg-white/90 shadow-apple-lg border border-violet-100 text-xs font-semibold text-violet-700 backdrop-blur-sm">
              24/7 Support
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─── SERVICES GRID ─── */
const ServicesSection = () => (
  <section className="py-16 sm:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        className="text-center max-w-3xl mx-auto"
      >
        <motion.span
          variants={fadeUp}
          className="text-sm font-semibold text-violet-600 uppercase tracking-wider"
        >
          Our Services
        </motion.span>
        <motion.h2
          variants={fadeUp}
          custom={1}
          className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900 leading-tight"
        >
          Expert services for when you need a partner, not just a platform
        </motion.h2>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        className="mt-12 sm:mt-16 space-y-6 sm:space-y-8"
      >
        {services.map((svc, i) => (
          <motion.div
            key={i}
            variants={fadeUp}
            custom={i}
            whileHover={{ y: -6, scale: 1.02 }}
            className="card-shine grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 sm:gap-8 items-start p-5 sm:p-8 rounded-2xl glass shadow-apple hover:shadow-apple-lg transition-all"
          >
            <div>
              <div className="flex items-center gap-4">
                <div
                  className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${svc.gradient}`}
                >
                  <svc.icon className="w-6 h-6 text-white" />
                </div>
                {svc.image && (
                  <img
                    src={svc.image}
                    alt={svc.title}
                    className="w-12 h-12 rounded-xl object-cover opacity-80"
                    loading="lazy"
                  />
                )}
              </div>
              <h3 className="mt-4 text-2xl font-bold text-slate-900">
                {svc.title}
              </h3>
              <p className="mt-3 text-slate-500 leading-relaxed max-w-2xl">
                {svc.desc}
              </p>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {svc.features.map((f, j) => (
                  <li
                    key={j}
                    className="flex items-center gap-2 text-sm text-slate-600"
                  >
                    <CheckCircle2 className="w-4 h-4 text-violet-600 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:text-right flex flex-row lg:flex-col items-center lg:items-end gap-4">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-medium">
                  Starting at
                </p>
                <p className="text-2xl font-black text-slate-900">
                  {svc.startingAt}
                </p>
                <p className="text-xs text-slate-400">per project</p>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-semibold hover:shadow-apple-lg transition-all"
              >
                Get Quote <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ─── PROCESS ─── */
const process = [
  {
    step: "01",
    title: "Discovery Call",
    desc: "We understand your business, goals, and challenges in a 30-minute call.",
  },
  {
    step: "02",
    title: "Proposal & Scope",
    desc: "We deliver a detailed proposal with timeline, deliverables, and transparent pricing.",
  },
  {
    step: "03",
    title: "Build & Iterate",
    desc: "Our team builds with weekly check-ins. You see progress, not surprises.",
  },
  {
    step: "04",
    title: "Launch & Support",
    desc: "We launch together and provide ongoing support to ensure lasting success.",
  },
];

const ProcessSection = () => (
  <section className="py-16 sm:py-24 bg-violet-tint">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        className="text-center max-w-3xl mx-auto"
      >
        <motion.span
          variants={fadeUp}
          className="text-sm font-semibold text-violet-600 uppercase tracking-wider"
        >
          How We Work
        </motion.span>
        <motion.h2
          variants={fadeUp}
          custom={1}
          className="mt-4 text-3xl sm:text-4xl font-bold text-slate-900 leading-tight"
        >
          Simple, transparent process
        </motion.h2>
      </motion.div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
        className="mt-10 sm:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8"
      >
        {process.map((p, i) => (
          <motion.div
            key={i}
            variants={fadeUp}
            custom={i}
            whileHover={{ y: -6, scale: 1.02 }}
            className="card-shine relative"
          >
            {i < process.length - 1 && (
              <div className="hidden lg:block absolute top-6 left-full w-full h-px border-t-2 border-dashed border-violet-200 -translate-x-6 animate-border-glow" />
            )}
            <span className="text-5xl font-black text-violet-100">
              {p.step}
            </span>
            <h3 className="mt-2 text-lg font-bold text-slate-900">{p.title}</h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">
              {p.desc}
            </p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

/* ─── WHY STUDIO ─── */
const WhyStudio = () => (
  <section className="py-16 sm:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
        >
          <motion.h2
            variants={fadeUp}
            className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight"
          >
            Why BusiGrow Studio?
          </motion.h2>
          <motion.div variants={fadeUp} custom={1} className="mt-8 space-y-6">
            {[
              {
                title: "We build what we sell",
                desc: "Our Studio team built the BusiGrow platform itself. You get the same engineering DNA for your custom projects.",
              },
              {
                title: "Market-native expertise",
                desc: "We know Indian and Middle Eastern business workflows intimately — GST, VAT, WhatsApp-first operations, regional payment systems.",
              },
              {
                title: "Platform + services synergy",
                desc: "Custom solutions integrate seamlessly with BusiGrow Automate. Your service project becomes a permanent automation asset.",
              },
              {
                title: "Transparent pricing, no surprises",
                desc: "Fixed-price projects with milestone payments. You know exactly what you're paying before we start.",
              },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="mt-1 w-6 h-6 rounded-full bg-violet-50 flex items-center justify-center flex-shrink-0">
                  <Star className="w-3.5 h-3.5 text-violet-600" />
                </div>
                <div>
                  <h4 className="text-base font-bold text-slate-800">
                    {item.title}
                  </h4>
                  <p className="text-sm text-slate-500 mt-1">{item.desc}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="rounded-2xl glass-strong border border-violet-100 overflow-hidden shadow-apple">
            <img
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80"
              alt="BusiGrow team collaborating on projects"
              className="w-full aspect-[4/3] object-cover img-parallax"
              loading="lazy"
            />
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─── CTA ─── */
const StudioCTA = () => (
  <section className="py-16 sm:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-2xl sm:rounded-[2rem] bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 p-8 sm:p-12 md:p-20 text-center shadow-apple-xl">
        <div className="absolute -top-20 -left-20 w-40 sm:w-60 h-40 sm:h-60 rounded-full bg-white/10 blur-2xl animate-float-orb" />
        <div className="absolute -bottom-20 -right-20 w-60 sm:w-80 h-60 sm:h-80 rounded-full bg-white/5 blur-3xl animate-float-orb-reverse" />
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4 sm:mb-5">
              Ready to bring your vision to life?
            </h2>
            <p className="text-base sm:text-lg text-white/70 max-w-xl mx-auto mb-8 sm:mb-10">
              Our studio team is ready to turn your ideas into reality. Let's
              create something extraordinary.
            </p>
            <Link
              to="/contact"
              className="btn-shine group inline-flex items-center gap-3 px-8 py-4 bg-white text-violet-700 font-semibold rounded-2xl hover:bg-violet-50 hover:scale-[1.02] transition-all duration-300 shadow-apple-lg"
            >
              Start Your Project{" "}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);

/* ─── PAGE ─── */
const StudioPage = () => (
  <div className="min-h-screen bg-[#FAFAFE] pb-24 md:pb-0">
    <Header />
    <StudioHero />
    <ServicesSection />
    <ProcessSection />
    <WhyStudio />
    <StudioCTA />
    <Footer />
  </div>
);

export default StudioPage;
